import { useEffect, useState } from "react";
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import * as yup from 'yup';

export default function PasswordRequirements({ password, onValidationChange }) {
    const passwordSchema = yup.string()
        .min(12, "At least 12 characters long")
        .matches(/[A-Z]/, "Contains at least one uppercase letter")
        .matches(/[a-z]/, "Contains at least one lowercase letter")
        .matches(/[0-9!@#$%^&*(),.?":{}|<>]/, "Contains at least one number or special character");

    const requirements = [
        {
            text: "At least 12 characters long",
            validator: async (password) => {
                try {
                    await yup.string().min(12).validate(password);
                    return true;
                } catch (err) {
                    return false;
                }
            },
        },
        {
            text: "Contains at least one uppercase letter",
            validator: async (password) => {
                try {
                    await yup.string().matches(/[A-Z]/).validate(password);
                    return true;
                } catch (err) {
                    return false;
                }
            },
        },
        {
            text: "Contains at least one lowercase letter",
            validator: async (password) => {
                try {
                    await yup.string().matches(/[a-z]/).validate(password);
                    return true;
                } catch (err) {
                    return false;
                }
            },
        },
        {
            text: "Contains at least one number or special character",
            validator: async (password) => {
                try {
                    await yup.string().matches(/[0-9!@#$%^&*(),.?":{}|<>]/).validate(password);
                    return true;
                } catch (err) {
                    return false;
                }
            },
        },
    ];

    // Check if all requirements are met
    const checkAllRequirements = async () => {
        try {
            await passwordSchema.validate(password || '');
            return true;
        } catch (err) {
            return false;
        }
    };

    // Notify parent component when validation status changes
    useEffect(() => {
        checkAllRequirements().then(allRequirementsMet => {
            onValidationChange(allRequirementsMet);
        });
    }, [password, onValidationChange]);

    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 1,
                bgcolor: 'grey.50',
                p: 1,
                mt: 2
            }}
        >
            <Typography variant="caption" color="textSecondary" sx={{ pl: 1 }}>
                Password Requirements:
            </Typography>
            <List dense sx={{ py: 0 }}>
                {requirements.map((req, index) => {
                    const [isValid, setIsValid] = useState(false);
                    
                    useEffect(() => {
                        req.validator(password || '').then(result => {
                            setIsValid(result);
                        });
                    }, [password, req]);

                    return (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon 
                                sx={{ 
                                    minWidth: 28,
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                {isValid ? (
                                    <CheckCircleOutlineIcon 
                                        color="success" 
                                        sx={{ fontSize: '1rem' }}
                                    />
                                ) : (
                                    <CancelIcon 
                                        color="error" 
                                        sx={{ fontSize: '1rem' }}
                                    />
                                )}
                            </ListItemIcon>
                            <ListItemText 
                                primary={req.text}
                                sx={{
                                    m: 0,
                                    '& .MuiListItemText-primary': {
                                        color: isValid ? 'success.main' : 'error.main',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            />
                        </ListItem>
                    );
                })}
            </List>
            <Typography 
                variant="caption" 
                color="textSecondary" 
                sx={{ 
                    display: 'block',
                    pl: 1,
                    pt: 0.5,
                    borderTop: 1,
                    borderColor: 'grey.300',
                    fontSize: '0.75rem',
                    fontStyle: 'italic'
                }}
            >
                Note: Don't reuse a previous password or the same password from another site. Avoid using your name, personal information, common words, repeated characters, or easily guessable patterns.
            </Typography>
        </Box>
    );
};
